// src/App.js
import React, { useEffect, useRef, useState } from "react";
import {
  getCurrentQuestDate,
  getCurrentTimestamp,
  getTodayDate,
} from "./utils/dateUtils";

import { Routes, Route, Navigate, Link } from "react-router-dom";

import Dashboard from "./components/Dashboard";
import Quests from "./components/Quests";
import Leaderboard from "./components/Leaderboard";
import SystemNotification from "./components/SystemNotification";
import SystemStatusBar from "./components/SystemStatusBar";
import SystemTerminals from "./components/SystemTerminals";
import SettingsMenu from "./components/SettingsMenu";
import LevelUpModal from "./components/LevelUpModal";
import HunterProfileSetup from "./components/HunterProfileSetup";
import ProfilePage from "./components/ProfilePage";
import { generateLevelUpPayload, markLevelUpShown } from "./utils/levelUpUtils";
import { buildSystemMemory, clearSystemMemoryCache } from "./utils/systemMemory";
import { getBrowserTimeZone, normalizeTimeZone } from "./utils/timezoneUtils";
import {
  initializeSystemDay,
  orchestrateBossQuest,
} from "./api/systemApi";
import {
  getCurrentAuthUser,
  getProfile,
  onSupabaseAuthChange,
  signInWithPassword,
  signUpWithPassword,
  subscribeToProfile,
  upsertProfile,
} from "./api/supabaseData";
import { primeSoundSystem } from "./audio/soundManager";

import "./styles/theme.css";

const AUTH_MODE = {
  SIGN_IN: "signIn",
  SIGN_UP: "signUp",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const USERNAME_PATTERN = /^[A-Za-z0-9_]+$/;
const USERNAME_MIN_LENGTH = 3;
const USERNAME_MAX_LENGTH = 16;
const USERNAME_RULE = `Username must be ${USERNAME_MIN_LENGTH}-${USERNAME_MAX_LENGTH} characters using letters, numbers, or underscores.`;
const PASSWORD_RULE =
  "Password must be at least 8 characters and include an uppercase letter, a lowercase letter, and a number.";

function normalizeEmail(email) {
  return String(email || "").trim().toLowerCase();
}

function normalizeUsername(username) {
  return String(username || "").trim();
}

function isStrongPassword(password) {
  return (
    typeof password === "string" &&
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[a-z]/.test(password) &&
    /\d/.test(password)
  );
}

function validateAuthForm(mode, form) {
  const username = normalizeUsername(form.username);
  const email = normalizeEmail(form.email);
  const password = String(form.password || "");
  const confirmPassword = String(form.confirmPassword || "");

  if (!email) return "Email is required.";
  if (!EMAIL_PATTERN.test(email)) return "Enter a valid email address.";
  if (!password) return "Password is required.";

  if (mode === AUTH_MODE.SIGN_IN) {
    if (password.length < 8) {
      return "Password must be at least 8 characters.";
    }
    return "";
  }

  if (!username) return "Username is required.";
  if (username.length < USERNAME_MIN_LENGTH || username.length > USERNAME_MAX_LENGTH) {
    return USERNAME_RULE;
  }
  if (!USERNAME_PATTERN.test(username)) {
    return USERNAME_RULE;
  }

  if (!isStrongPassword(password)) {
    return PASSWORD_RULE;
  }

  if (!confirmPassword) return "Confirm your password.";
  if (password !== confirmPassword) return "Passwords do not match.";

  return "";
}

function looksLikeEmail(value) {
  return EMAIL_PATTERN.test(String(value || "").trim());
}

function isStaleAuthProfileError(error) {
  const code = String(error?.code || "");
  const message = `${error?.message || ""} ${error?.details || ""}`;

  return (
    code === "23503" &&
    /profiles_id_fkey|key is not present in table "users"/i.test(message)
  );
}

function getAuthDisplayName(user) {
  const candidate = normalizeUsername(
    user?.rawUser?.user_metadata?.user_name ||
      user?.rawUser?.user_metadata?.full_name ||
      user?.rawUser?.user_metadata?.name ||
      user?.displayName ||
      ""
  );

  if (!candidate || looksLikeEmail(candidate)) {
    return "";
  }

  return candidate;
}

function App() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authMode, setAuthMode] = useState(AUTH_MODE.SIGN_IN);
  const [authForm, setAuthForm] = useState({
    username: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [authSubmitting, setAuthSubmitting] = useState(false);
  const [authError, setAuthError] = useState("");
  const [authSuccess, setAuthSuccess] = useState("");
  const [showNotification, setShowNotification] = useState(true);
  const [systemEvents, setSystemEvents] = useState([]);
  const [todaysQuests, setTodaysQuests] = useState([]);
  const [notificationReady, setNotificationReady] = useState(false);
  const [notificationPenaltyMode, setNotificationPenaltyMode] = useState(false);
  const [notificationPenaltyQuestName, setNotificationPenaltyQuestName] = useState("");
  const [penaltyChecked, setPenaltyChecked] = useState(false);
  const [lvlModalOpen, setLvlModalOpen] = useState(false);
  const [lvlReport, setLvlReport] = useState(null);
  const [lvlLoading, setLvlLoading] = useState(false);
  const checkingLevelUpRef = useRef(false);
  const sessionShownLevelsRef = useRef(new Set());
  const systemDayInitInFlightRef = useRef(false);

  useEffect(() => {
    primeSoundSystem();
  }, []);

  // Supabase auth
  useEffect(() => {
    let mounted = true;

    getCurrentAuthUser()
      .then((currentUser) => {
        if (!mounted) return;
        setUser(currentUser);
        setAuthLoading(false);
      })
      .catch((error) => {
        console.error("Failed to restore auth session:", error);
        if (!mounted) return;
        setUser(null);
        setAuthLoading(false);
      });

    const unsubscribe = onSupabaseAuthChange((nextUser) => {
      if (!mounted) return;
      setUser(nextUser);
      setAuthLoading(false);
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  // Profile (live updates)
  useEffect(() => {
    if (!user?.uid) {
      setUserProfile(null);
      return;
    }
    let active = true;
    let unsubscribe = () => {};

    (async () => {
      try {
        const existingProfile = await getProfile(user.uid);
        const authDisplayName = getAuthDisplayName(user);

        if (!existingProfile) {
          const defaultProfile = {
            displayName: authDisplayName,
            photoURL: user.photoURL || "",
            totalXP: 0,
            level: 1,
            currentTitle: "Weakest Hunter of Mankind",
            wakeTime: "",
            sleepTime: "",
            timeZone: getBrowserTimeZone(),
            longTermGoal: "",
            hunterProfile: "",
            questsPerDay: 5,
            preferredFocusMinutes: null,
            nonNegotiables: "",
            frictionPoints: "",
            onboardingCompleted: false,
            createdAt: getCurrentTimestamp(),
            lastLevelUpPopupLevel: 0,
          };

          const createdProfile = await upsertProfile(user.uid, defaultProfile);
          if (active) {
            setUserProfile(createdProfile);
          }
        } else {
          const needsIdentityRefresh =
            (authDisplayName && existingProfile.displayName !== authDisplayName) ||
            existingProfile.photoURL !== (user.photoURL || "");

          if (needsIdentityRefresh) {
            const refreshedProfile = await upsertProfile(user.uid, {
              ...(authDisplayName ? { displayName: authDisplayName } : {}),
              photoURL: user.photoURL || "",
            });
            if (active) {
              setUserProfile(refreshedProfile);
            }
          } else if (active) {
            setUserProfile(existingProfile);
          }
        }

        unsubscribe = subscribeToProfile(user.uid, (profile) => {
          if (active && profile) {
            setUserProfile(profile);
          }
        });
      } catch (error) {
        console.error("Failed to hydrate profile:", error);
        if (isStaleAuthProfileError(error)) {
          setAuthMode(AUTH_MODE.SIGN_IN);
          setAuthSuccess("");
          setAuthError("Stored session was invalid. Sign in again.");
          setUserProfile(null);
          try {
            await signOutUser();
          } catch (signOutError) {
            console.error("Failed to clear stale auth session:", signOutError);
          }
          if (active) {
            setUser(null);
          }
        }
      }
    })();

    return () => {
      active = false;
      unsubscribe();
    };
  }, [user?.uid, user?.displayName, user?.photoURL]);

  // Single authoritative level-up flow
  useEffect(() => {
    const run = async () => {
      if (!user?.uid || !userProfile?.level) return;

      const currentLevel = Number(userProfile.level) || 1;
      const lastShown = Number(userProfile.lastLevelUpPopupLevel || 0);

      if (
        currentLevel <= lastShown ||
        sessionShownLevelsRef.current.has(currentLevel) ||
        checkingLevelUpRef.current
      ) {
        return;
      }

      const seedReport = {
        level: currentLevel,
        title: userProfile.currentTitle || `Level ${currentLevel}`,
        motivation: "",
        criticism: [],
      };

      try {
        checkingLevelUpRef.current = true;
        sessionShownLevelsRef.current.add(currentLevel);
        setLvlReport(seedReport);
        setLvlLoading(true);
        setLvlModalOpen(true);

        const report = await generateLevelUpPayload(user.uid, currentLevel);
        setLvlReport(report);
      } catch (error) {
        console.error("Failed to build level-up report:", error);
        setLvlReport(seedReport);
      } finally {
        checkingLevelUpRef.current = false;
        setLvlLoading(false);
      }
    };

    run();
  }, [user?.uid, userProfile?.level, userProfile?.lastLevelUpPopupLevel, userProfile?.currentTitle]);

  const onCloseLevelUp = async () => {
    const shownLevel = Number(lvlReport?.level || userProfile?.level || 0);
    setLvlModalOpen(false);
    setLvlLoading(false);
    if (user?.uid && shownLevel > 0) {
      try {
        await markLevelUpShown(user.uid, shownLevel);
      } catch {}
    }
  };

  const handleProfileUpdate = (nextProfile) => {
    if (!nextProfile) return;
    setUserProfile((currentProfile) => ({
      ...(currentProfile || {}),
      ...nextProfile,
    }));
  };

  const handleSaveHunterProfile = async (payload) => {
    if (!user?.uid) return;
    const isCompletingOnboarding =
      userProfile?.onboardingCompleted === false && payload?.onboardingCompleted === true;
    const updatedProfile = await upsertProfile(user.uid, payload);
    if (isCompletingOnboarding) {
      const storageKey = `penaltyLastCheck:${user.uid}`;
      localStorage.removeItem(storageKey);
      setSystemEvents([]);
      setTodaysQuests([]);
      setPenaltyChecked(false);
      setShowNotification(true);
      setNotificationReady(false);
      setNotificationPenaltyMode(false);
      setNotificationPenaltyQuestName("");
      systemDayInitInFlightRef.current = false;
    }
    clearSystemMemoryCache(user.uid);
    buildSystemMemory(user.uid).catch((error) => {
      console.error("Failed to refresh system memory after calibration:", error);
    });
    setUserProfile(updatedProfile);
  };

  const currentQuestDate = getCurrentQuestDate();
  const pendingPenaltyQuests = todaysQuests.filter(
    (quest) =>
      quest?.isPenalty &&
      quest?.status !== "failed" &&
      quest?.claimed !== true
  );

  // Penalties check once per day after login
  useEffect(() => {
    setSystemEvents([]);
    setTodaysQuests([]);
    setPenaltyChecked(false);
    setShowNotification(Boolean(user));
    setNotificationReady(!user);
    setNotificationPenaltyMode(false);
    setNotificationPenaltyQuestName("");
    setLvlModalOpen(false);
    setLvlReport(null);
    setLvlLoading(false);
    sessionShownLevelsRef.current = new Set();
    systemDayInitInFlightRef.current = false;
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) {
      clearSystemMemoryCache();
      return;
    }

    buildSystemMemory(user.uid).catch((error) => {
      console.error("Failed to prime system memory:", error);
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid || !userProfile) return;
    if (userProfile.timeZone) return;

    const browserTimeZone = normalizeTimeZone(getBrowserTimeZone());
    upsertProfile(user.uid, { timeZone: browserTimeZone })
      .then((updatedProfile) => {
        setUserProfile(updatedProfile);
      })
      .catch((error) => {
        console.error("Failed to backfill profile timezone:", error);
      });
  }, [user?.uid, userProfile]);

  useEffect(() => {
    if (!user?.uid || !userProfile || userProfile.onboardingCompleted === false || penaltyChecked) return;
    if (systemDayInitInFlightRef.current) return;

    let cancelled = false;
    let retryTimerId = null;
    let retryCount = 0;
    const todayStr = getTodayDate();
    const storageKey = `penaltyLastCheck:${user.uid}`;
    const lastCheck = localStorage.getItem(storageKey);
    const shouldAnnounceEvents = lastCheck !== todayStr;
    const applyNotificationState = (penaltyQuest = null, fallbackEvent = null) => {
      const penaltyName =
        penaltyQuest?.sourceQuestTitle ||
        penaltyQuest?.title ||
        fallbackEvent?.sourceQuestTitle ||
        fallbackEvent?.title ||
        "";
      setNotificationPenaltyMode(Boolean(penaltyQuest || fallbackEvent));
      setNotificationPenaltyQuestName(penaltyName);
      setNotificationReady(true);
    };

    const runInitialization = async () => {
      systemDayInitInFlightRef.current = true;

      try {
        const { events, pendingPenaltyQuest, profile, quests, today } = await initializeSystemDay({
          announceEvents: shouldAnnounceEvents,
        });

        if (cancelled) return;

        setSystemEvents(shouldAnnounceEvents ? events : []);
        if (Array.isArray(quests)) {
          setTodaysQuests(quests);
        }
        if (profile) {
          handleProfileUpdate(profile);
        }
        localStorage.setItem(storageKey, today || todayStr);
        const fallbackPenaltyEvent =
          events.find((event) => event.kind === "penaltyQuest") ||
          events.find((event) => event.kind === "xpDeduction") ||
          null;
        applyNotificationState(pendingPenaltyQuest, fallbackPenaltyEvent);
        setPenaltyChecked(true);
      } catch (error) {
        if (cancelled) return;

        const message = String(error?.message || "");
        const shouldRetryAuth =
          /system session is not ready yet/i.test(message) ||
          /invalid jwt/i.test(message);

        if (shouldRetryAuth && retryCount < 4) {
          retryCount += 1;
          retryTimerId = window.setTimeout(() => {
            if (!cancelled) {
              runInitialization();
            }
          }, retryCount * 400);
          return;
        }

        console.error("System day initialization failed:", error);
        applyNotificationState(null);
        setPenaltyChecked(true);
      } finally {
        systemDayInitInFlightRef.current = false;
      }
    };

    runInitialization();

    return () => {
      cancelled = true;
      if (retryTimerId) {
        window.clearTimeout(retryTimerId);
      }
      systemDayInitInFlightRef.current = false;
    };
  }, [user?.uid, userProfile?.onboardingCompleted, penaltyChecked]);

  useEffect(() => {
    if (!user?.uid || !userProfile?.autoBossQuest) return;

    orchestrateBossQuest({
      mode: "auto",
      userLevel: userProfile?.level || 1,
    }).catch((error) => {
      console.error("Failed to ensure weekly boss quest:", error);
    });
  }, [user?.uid, userProfile?.autoBossQuest, userProfile?.level]);

  const handleAuthFieldChange = (field) => (event) => {
    const value = event.target.value;
    setAuthForm((current) => ({ ...current, [field]: value }));
    if (authError) setAuthError("");
    if (authSuccess) setAuthSuccess("");
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setAuthError("");
    setAuthSuccess("");
    setAuthForm((current) => ({
      username: current.username,
      email: current.email,
      password: "",
      confirmPassword: "",
    }));
  };

  const handleAuthSubmit = async (event) => {
    event.preventDefault();

    const validationError = validateAuthForm(authMode, authForm);
    if (validationError) {
      setAuthError(validationError);
      setAuthSuccess("");
      return;
    }

    const email = normalizeEmail(authForm.email);
    const username = normalizeUsername(authForm.username);
    const password = authForm.password;

    setAuthSubmitting(true);
    setAuthError("");
    setAuthSuccess("");

    try {
      if (authMode === AUTH_MODE.SIGN_IN) {
        await signInWithPassword(email, password);
        return;
      }

      const result = await signUpWithPassword(email, password, username);
      const needsEmailVerification = !result?.session;

      setAuthSuccess(
        needsEmailVerification
          ? "Account created. Check your inbox and confirm your email before signing in."
          : "Account created. Entering the System..."
      );
      setAuthForm({
        username: "",
        email,
        password: "",
        confirmPassword: "",
      });

      if (needsEmailVerification) {
        setAuthMode(AUTH_MODE.SIGN_IN);
      }
    } catch (error) {
      console.error("Authentication failed:", error);
      setAuthError(error?.message || "Authentication failed. Try again.");
    } finally {
      setAuthSubmitting(false);
    }
  };

  useEffect(() => {
    if (!user || !showNotification || !notificationReady) return undefined;
    const timeoutId = window.setTimeout(() => setShowNotification(false), 2500);
    return () => window.clearTimeout(timeoutId);
  }, [notificationReady, user, showNotification]);

  // Auth loading screen
  if (authLoading) {
    return (
      <div className="loading-bg">
        <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />
        <div className="login-bg">
          <div className="login-card" style={{ opacity: 0.93 }}>
            <div className="auth-brand" style={{ marginBottom: 10 }}>
              <div className="auth-brand__title">QuestXP</div>
              <div className="auth-brand__subtitle">The Monarch System</div>
            </div>
            <div className="shadow-loader">
              <div className="shadow-orb" />
              <div className="shadow-pulse" />
            </div>
            <div style={{ textAlign: "center", color: "#e2eaff", fontWeight: 600, fontSize: "1.13em", marginTop: 32, opacity: 0.7, letterSpacing: 0.5 }}>
              Syncing with the System...
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (user && !userProfile) {
    return (
      <div className="loading-bg">
        <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />
        <div className="login-bg">
          <div className="login-card" style={{ opacity: 0.93 }}>
            <h1 style={{ fontFamily: "Orbitron", marginBottom: 28, letterSpacing: 1.5 }}>
              The Monarch System
            </h1>
            <div className="shadow-loader">
              <div className="shadow-orb" />
              <div className="shadow-pulse" />
            </div>
            <div style={{ textAlign: "center", color: "#e2eaff", fontWeight: 600, fontSize: "1.13em", marginTop: 32, opacity: 0.7, letterSpacing: 0.5 }}>
              Retrieving hunter profile...
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Login screen
  if (!user) {
    return (
      <>
        <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />
        <div className="login-bg">
          <div className="login-card">
            <div className="auth-brand">
              <div className="auth-brand__title">QuestXP</div>
              <div className="auth-brand__subtitle">The Monarch System</div>
            </div>
            <p className="login-card__copy">
              Enter the System with your email and password.
            </p>

            <div className="auth-tabs" role="tablist" aria-label="Authentication mode">
              <button
                type="button"
                className={`auth-tabs__btn ${authMode === AUTH_MODE.SIGN_IN ? "is-active" : ""}`}
                onClick={() => switchAuthMode(AUTH_MODE.SIGN_IN)}
              >
                Sign In
              </button>
              <button
                type="button"
                className={`auth-tabs__btn ${authMode === AUTH_MODE.SIGN_UP ? "is-active" : ""}`}
                onClick={() => switchAuthMode(AUTH_MODE.SIGN_UP)}
              >
                Sign Up
              </button>
            </div>

            <form className="auth-form" onSubmit={handleAuthSubmit} noValidate>
              {authMode === AUTH_MODE.SIGN_UP && (
                <>
                  <div className="auth-form__field">
                    <label htmlFor="auth-username">Username</label>
                    <input
                      id="auth-username"
                      type="text"
                      autoComplete="username"
                      value={authForm.username}
                      onChange={handleAuthFieldChange("username")}
                      placeholder="Choose a username"
                      maxLength={USERNAME_MAX_LENGTH}
                    />
                  </div>
                  <p className="auth-form__hint">{USERNAME_RULE}</p>
                </>
              )}

              <div className="auth-form__field">
                <label htmlFor="auth-email">Email</label>
                <input
                  id="auth-email"
                  type="email"
                  autoComplete="email"
                  value={authForm.email}
                  onChange={handleAuthFieldChange("email")}
                  placeholder="hunter@system.com"
                />
              </div>

              <div className="auth-form__field">
                <label htmlFor="auth-password">Password</label>
                <input
                  id="auth-password"
                  type="password"
                  autoComplete={authMode === AUTH_MODE.SIGN_IN ? "current-password" : "new-password"}
                  value={authForm.password}
                  onChange={handleAuthFieldChange("password")}
                  placeholder="Enter your password"
                />
              </div>

              {authMode === AUTH_MODE.SIGN_UP && (
                <>
                  <p className="auth-form__hint">{PASSWORD_RULE}</p>
                  <div className="auth-form__field">
                    <label htmlFor="auth-confirm-password">Confirm Password</label>
                    <input
                      id="auth-confirm-password"
                      type="password"
                      autoComplete="new-password"
                      value={authForm.confirmPassword}
                      onChange={handleAuthFieldChange("confirmPassword")}
                      placeholder="Re-enter your password"
                    />
                  </div>
                </>
              )}

              {authError && <p className="auth-form__message auth-form__message--error">{authError}</p>}
              {authSuccess && (
                <p className="auth-form__message auth-form__message--success">{authSuccess}</p>
              )}

              <button className="login-btn auth-form__submit" type="submit" disabled={authSubmitting}>
                {authSubmitting
                  ? authMode === AUTH_MODE.SIGN_IN
                    ? "Signing In..."
                    : "Creating Account..."
                  : authMode === AUTH_MODE.SIGN_IN
                    ? "Sign In"
                    : "Create Account"}
              </button>
            </form>
          </div>
        </div>
      </>
    );
  }

  if (userProfile && userProfile.onboardingCompleted === false) {
    return (
      <HunterProfileSetup
        profile={userProfile}
        onSave={handleSaveHunterProfile}
      />
    );
  }

  // Post-login system notification (2.5s)
  if (showNotification) {
    return (
      <>
        <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />
        <SystemNotification
          ready={notificationReady}
          penaltyMode={notificationPenaltyMode}
          penaltyQuestName={notificationPenaltyQuestName}
        />
      </>
    );
  }

  return (
    <>
      {/* Background video */}
      <video autoPlay loop muted playsInline className="video-bg" src="/sung-jinwoo.mp4" type="video/mp4" />

      {/* App shell */}
      <div className="app-bg">
        <nav className="navbar" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Link to="/" style={{ color: "inherit" }}>
            <div className="navbar__brand">
              <div className="navbar__brand-title">QuestXP</div>
              <div className="navbar__brand-subtitle">The Monarch System</div>
            </div>
          </Link>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <SettingsMenu userLevel={userProfile?.level || 1} />
          </div>
        </nav>
        {/* Penalty Notification */}
        {systemEvents.length > 0 && (
          <div className="penalty-notification system-event-feed">
            <b className="system-event-feed__title">[SYSTEM LOG] Daily cycle updates recorded.</b>
            <ul className="system-event-feed__list">
              {systemEvents.map((event) => {
                if (event.kind === "systemAward") {
                  const headline =
                    event.awardType === "streak"
                      ? `${event.days}-day streak confirmed. Bonus XP dispensed.`
                      : "Daily board cleared. Bonus XP issued.";

                  return (
                    <li key={event.id} className="system-event-feed__item system-event-feed__item--award">
                      <div className="system-event-feed__label">[SYSTEM AWARD]</div>
                      <div className="system-event-feed__headline">{headline}</div>
                      <div className="system-event-feed__copy">+{event.xpAwarded} XP transferred to your record.</div>
                    </li>
                  );
                }

                if (event.kind === "xpDeduction") {
                  return (
                    <li key={event.id} className="system-event-feed__item system-event-feed__item--deduction">
                      <div className="system-event-feed__label">[SYSTEM PENALTY]</div>
                      <div className="system-event-feed__headline">
                        Penalty mandate ignored. XP has been removed.
                      </div>
                      <div className="system-event-feed__copy">
                        -{event.xpRemoved} XP. {event.desc}
                      </div>
                    </li>
                  );
                }

                return (
                  <li key={event.id} className="system-event-feed__item system-event-feed__item--penalty">
                    <div className="system-event-feed__label">[SYSTEM MANDATE]</div>
                    <div className="system-event-feed__headline">
                      {event.sourceQuestTitle
                        ? `${event.sourceQuestTitle} was left unresolved. A mandate has been issued.`
                        : `${event.title} has been issued by the System.`}
                    </div>
                    <div className="system-event-feed__copy">
                      {event.title} for {event.xp} XP. Non-completion will be recorded.
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Pages */}
        <div className="page">
          <Routes>
            <Route
              path="/"
              element={
                <>
                  <Dashboard user={user} userProfile={userProfile} />
                  <SystemStatusBar
                    profile={userProfile}
                    todaysQuests={todaysQuests}
                    pendingPenalties={pendingPenaltyQuests}
                    questDate={currentQuestDate}
                  />
                  <Quests
                    userLevel={userProfile?.level || 1}
                    user={user}
                    userProfile={userProfile}
                    onProfileUpdate={handleProfileUpdate}
                    onQuestStateChange={setTodaysQuests}
                  />
                  <SystemTerminals
                    user={user}
                    userProfile={userProfile}
                    onProfileUpdate={handleProfileUpdate}
                  />
                </>
              }
            />
            <Route
              path="/profile"
              element={
                <ProfilePage
                  user={user}
                  profile={userProfile}
                  onSave={handleSaveHunterProfile}
                />
              }
            />
            <Route path="/leaderboard" element={<Leaderboard user={user} />} />
            {/* Optional: separate routes */}
            {/* <Route path="/quests" element={<Quests user={user} userProfile={userProfile} />} /> */}
            {/* <Route path="/analytics" element={<div className="stats-card"><AnalyticsCard user={user} /></div>} /> */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </div>

      <LevelUpModal
        show={lvlModalOpen}
        onClose={onCloseLevelUp}
        report={lvlReport}
        loading={lvlLoading}
      />
    </>
  );
}

export default App;
